// The rules that decide whether an integration runs. Operators that carry their
// own meaning fill in the value column for the user.

import { useCallback, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { t } from '@lingui/macro';

const EMPTY_CONDITIONS = [{ condition_type: 'and', rules: [] }];

const EMPTY_RULE = { condition_attribute: '', rule: '', condition_value: '' };

// These operators are self-contained, so the value column is filled in for the
// user rather than left for them to guess at.
const VALUELESS_OPERATORS = [
  'is_present',
  'is_not_present',
  'is_true',
  'is_false',
];

const conditionsOf = (attributes) =>
  isEmpty(attributes?.execution_conditions)
    ? cloneDeep(EMPTY_CONDITIONS)
    : cloneDeep(attributes.execution_conditions);

// Every column of a rule is editable, so none of them can key the row; keying by
// index makes React hand a removed row's DOM node to its successor. The id is
// internal — toPayload strips it so the request body is unchanged.
const withoutRowId = ({ rowId: _rowId, ...rule }) => rule;

const allRulesComplete = (conditions) =>
  isEmpty(conditions) ||
  conditions.every((group) =>
    group.rules.every(
      (rule) =>
        !isEmpty(trim(rule.condition_attribute || '')) &&
        !isEmpty(trim(rule.rule || '')) &&
        !isEmpty(rule.condition_value),
    ),
  );

export default function useExecutionConditions({
  integration,
  setDisableSubmit,
  setError,
}) {
  const nextRowId = useRef(0);
  const mintId = useCallback(() => {
    nextRowId.current += 1;
    return `condition-row-${nextRowId.current}`;
  }, []);
  const groupsOf = useCallback(
    (attributes) =>
      conditionsOf(attributes).map((group) => ({
        ...group,
        rules: group.rules.map((rule) => ({ ...rule, rowId: mintId() })),
      })),
    [mintId],
  );

  const [executionConditions, setExecutionConditions] = useState(() =>
    groupsOf(integration.attributes),
  );

  // `mapRules` receives the current rules and returns the replacement list.
  const updateRules = useCallback(
    (mapRules) => {
      setExecutionConditions((previous) => {
        const next = [
          { ...previous[0], rules: mapRules(previous[0].rules) },
          ...previous.slice(1),
        ];
        setDisableSubmit(!allRulesComplete(next));
        return next;
      });
    },
    [setDisableSubmit],
  );

  const handlers = useMemo(
    () => ({
      addEntry: () => {
        setExecutionConditions((previous) => [
          {
            ...previous[0],
            rules: [...previous[0].rules, { ...EMPTY_RULE, rowId: mintId() }],
          },
          ...previous.slice(1),
        ]);
        setDisableSubmit(true);
      },
      changeText: ({ e, index }) => {
        const { value } = e.target;
        const field = e.currentTarget.name.split('-')[1];
        const blank = isEmpty(trim(value));

        setError(e.target.name, blank ? t`Can't be blank` : '');
        setDisableSubmit(blank);

        updateRules((rules) =>
          rules.map((rule, i) => {
            if (i !== index) return rule;
            if (field === 'attribute')
              return { ...rule, condition_attribute: value };
            if (field === 'value') return { ...rule, condition_value: value };
            return rule;
          }),
        );
      },
      changeOption: ({ e, index }) => {
        updateRules((rules) =>
          rules.map((rule, i) =>
            i === index
              ? {
                  ...rule,
                  rule: e,
                  condition_value: VALUELESS_OPERATORS.includes(e)
                    ? '---'
                    : rule.condition_value,
                }
              : rule,
          ),
        );
      },
      removeEntry: (index) => {
        updateRules((rules) => rules.filter((_rule, i) => i !== index));
      },
    }),
    [updateRules, setDisableSubmit, setError, mintId],
  );

  const setConditionType = useCallback((conditionType) => {
    setExecutionConditions((previous) => [
      { ...previous[0], condition_type: conditionType },
      ...previous.slice(1),
    ]);
  }, []);

  const reset = useCallback(
    (data) => {
      setExecutionConditions(groupsOf(data?.attributes));
    },
    [groupsOf],
  );

  // Empty groups carry no meaning to the backend.
  const toPayload = useCallback(
    () =>
      executionConditions
        .filter((condition) => condition.rules.length > 0)
        .map((condition) => ({
          ...condition,
          rules: condition.rules.map(withoutRowId),
        })),
    [executionConditions],
  );

  return {
    executionConditions,
    handlers,
    setConditionType,
    reset,
    toPayload,
  };
}
